// Self-service toggle: "text MY mobile when a prospect replies to my SMS".
// Managers additionally get a Team section: set any member's mobile/toggle
// and a one-click "enable all with a mobile" switch.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRing, Loader2, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = ['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor', 'office_manager'];

const TeamAlerts = () => {
  const [team, setTeam] = useState(null);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/sms/alert-settings/team`).then((r) => setTeam(r.data)).catch(() => setTeam(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveMember = async (m, patch) => {
    setBusy(m.user_id);
    try {
      await axios.put(`${API}/sms/alert-settings/team`, { user_id: m.user_id, ...patch });
      toast.success(`${m.name}: alert settings saved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(null); }
  };

  const enableAll = async (enabled) => {
    setBusy('all');
    try {
      const r = await axios.put(`${API}/sms/alert-settings/team`, { all: true, enabled });
      toast.success(`${enabled ? 'Enabled' : 'Disabled'} SMS alerts for ${r.data.matched} member${r.data.matched === 1 ? '' : 's'} with a mobile`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk update failed'); }
    finally { setBusy(null); }
  };

  if (!team) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid="sms-alert-team-section">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" /> Team ({team.enabled_count}/{team.members.length} on)
        </p>
        <div className="flex gap-1.5">
          <button onClick={() => enableAll(true)} disabled={busy === 'all'}
            className="text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
            data-testid="sms-alert-enable-all-btn">Enable all</button>
          <button onClick={() => enableAll(false)} disabled={busy === 'all'}
            className="text-[10px] font-bold px-2 py-1 rounded-full bg-slate-200 text-slate-600 hover:bg-slate-300 disabled:opacity-40"
            data-testid="sms-alert-disable-all-btn">Disable all</button>
        </div>
      </div>
      <p className="text-[10px] text-slate-400 mb-1.5">"Enable all" switches on everyone who has a mobile set below.</p>
      <div className="max-h-52 overflow-y-auto space-y-1" data-testid="sms-alert-team-list">
        {team.members.map((m) => (
          <div key={m.user_id} className="flex items-center gap-1.5" data-testid={`sms-alert-team-row-${m.user_id}`}>
            <span className="text-[11px] text-slate-700 flex-1 truncate" title={m.name}>{m.name}</span>
            <input defaultValue={m.mobile}
              onChange={(e) => setEdits((p) => ({ ...p, [m.user_id]: e.target.value }))}
              onBlur={() => { const v = edits[m.user_id]; if (v !== undefined && v !== m.mobile) saveMember(m, { mobile: v }); }}
              placeholder="04xx…"
              className="w-28 text-[11px] border border-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-900"
              data-testid={`sms-alert-team-mobile-${m.user_id}`} />
            <input type="checkbox" checked={m.enabled} disabled={busy === m.user_id}
              onChange={(e) => saveMember(m, { enabled: e.target.checked })}
              title={m.enabled ? 'Alerts on' : 'Alerts off'}
              data-testid={`sms-alert-team-toggle-${m.user_id}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const SmsAlertSettings = () => {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sms/alert-settings`).then((r) => {
      setMobile(r.data.mobile || '');
      setEnabled(!!r.data.enabled);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/sms/alert-settings`, { mobile, enabled });
      setMobile(r.data.mobile || '');
      toast.success(r.data.enabled
        ? `SMS reply alerts ON — you'll be texted at ${r.data.mobile}`
        : 'SMS reply alerts turned off');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save alert settings');
    } finally { setSaving(false); }
  };

  return (
    <div className="relative" data-testid="sms-alert-settings">
      <button onClick={() => setOpen(!open)}
        className={`text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 ${enabled ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
        title="Get an SMS on your own phone when a prospect replies to you"
        data-testid="sms-alert-settings-toggle">
        <BellRing className="w-3.5 h-3.5" /> SMS alerts {enabled ? 'on' : 'off'}
      </button>
      {open && (
        <div className={`absolute right-0 top-9 z-30 ${isManager ? 'w-96' : 'w-72'} bg-white border border-slate-200 rounded-xl shadow-lg p-3 space-y-2`}
          data-testid="sms-alert-settings-panel">
          <p className="text-xs font-semibold text-slate-700">Text me when a prospect replies</p>
          <p className="text-[11px] text-slate-400">Sent to your own mobile when someone replies to an SMS you sent. Max 1 alert per 3 minutes.</p>
          <div className="flex items-center gap-2">
            <input value={mobile} onChange={(e) => setMobile(e.target.value)}
              placeholder="Your mobile e.g. 04xx xxx xxx"
              className="flex-1 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 font-mono text-slate-900"
              data-testid="sms-alert-mobile-input" />
            <label className="text-xs text-slate-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                data-testid="sms-alert-enabled-toggle" /> on
            </label>
            <button onClick={save} disabled={saving}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="sms-alert-save-btn">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
            </button>
          </div>
          {isManager && <TeamAlerts />}
        </div>
      )}
    </div>
  );
};

export default SmsAlertSettings;
